/**
 * IMAGE UPLOAD FIELD (TS-95) — campo condiviso per caricare un'immagine con
 * ritaglio, estratto dalla tab Profilo di AccountSettings e riusato dal logo
 * azienda (CompanySettings). Incapsula: anteprima (Avatar), bottone "Carica",
 * lettura del file via FileReader, ritaglio via {@link AvatarCropDialog} e il
 * bottone "Riposiziona" per riaprire il cropper sull'originale.
 *
 * Componente CONTROLLATO: il chiamante tiene `value` (data URL del ritaglio, o
 * null) e riceve i cambi via `onChange`. Quando `value` torna null (es. dopo il
 * salvataggio) l'originale interno viene azzerato. In sola lettura (`disabled`)
 * mostra solo l'anteprima, senza controlli di upload.
 */

import { useEffect, useState, type ChangeEvent } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import { AvatarCropDialog } from './AvatarCropDialog';

interface ImageUploadFieldProps {
  /** Data URL del ritaglio corrente, oppure null (nessuna nuova immagine scelta). */
  value: string | null;
  /** Notifica il nuovo ritaglio (data URL) al chiamante. */
  onChange: (dataUrl: string | null) => void;
  /** Immagine esistente da mostrare quando `value` è null (es. avatar/logo attuale). */
  currentSrc?: string;
  /** Etichetta del bottone di selezione file. */
  selectLabel: string;
  /** Etichetta del bottone "Riposiziona" (riapre il cropper sull'originale). */
  repositionLabel: string;
  alt?: string;
  /** Sola lettura: nasconde i controlli di upload, resta solo l'anteprima. */
  disabled?: boolean;
  /** Forma dell'anteprima: 'circular' (profilo) o 'rounded' (logo). */
  shape?: 'circular' | 'rounded';
}

export function ImageUploadField({
  value,
  onChange,
  currentSrc,
  selectLabel,
  repositionLabel,
  alt = '',
  disabled = false,
  shape = 'circular',
}: ImageUploadFieldProps) {
  // originalSrc = immagine caricata non ritagliata (per riposizionarla);
  // cropSrc apre il cropper. Transienti, interni al widget.
  const [originalSrc, setOriginalSrc] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  // Il chiamante azzera `value` (es. dopo il salvataggio) -> azzera l'originale
  // così "Riposiziona" sparisce.
  useEffect(() => {
    if (value === null) setOriginalSrc(null);
  }, [value]);

  function handlePick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // reset value: permette di ri-selezionare lo stesso file.
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      setOriginalSrc(url);
      setCropSrc(url); // apre il cropper per posizionare l'immagine
    };
    reader.readAsDataURL(file);
  }

  const displaySrc = value ?? currentSrc ?? undefined;

  return (
    <div className="image-upload">
      <Avatar src={displaySrc} alt={alt} variant={shape} className="image-upload__avatar" />
      {!disabled && (
        <div className="image-upload__actions">
          <Button variant="outlined" component="label" size="small">
            {selectLabel}
            <input type="file" accept="image/*" hidden onChange={handlePick} />
          </Button>
          {originalSrc && (
            <Button variant="text" size="small" onClick={() => setCropSrc(originalSrc)}>
              {repositionLabel}
            </Button>
          )}
        </div>
      )}

      {cropSrc && (
        <AvatarCropDialog
          src={cropSrc}
          shape={shape === 'circular' ? 'circular' : 'square'}
          onCancel={() => setCropSrc(null)}
          onConfirm={(cropped) => {
            onChange(cropped);
            setCropSrc(null);
          }}
        />
      )}
    </div>
  );
}
